import React, { useState, useEffect } from 'react';
import type { Session } from '@supabase/supabase-js';
const ReportDisplay = React.lazy(() => import('./ReportDisplay').then(m => ({ default: m.ReportDisplay })));
const OrderFlowDisplay = React.lazy(() => import('./OrderFlowDisplay').then(m => ({ default: m.OrderFlowDisplay })));
const TechnicalChartDisplay = React.lazy(() => import('./TechnicalChartDisplay').then(m => ({ default: m.TechnicalChartDisplay })));
const CotDisplay = React.lazy(() => import('./CotDisplay').then(m => ({ default: m.CotDisplay })));
import { NewsList } from './NewsList';
import { SignalsPreview } from './SignalsPreview';
import { useNavigate } from 'react-router-dom';
import { analyzeForexPair, analyzeOrderFlow, analyzeTechnicalTrend, analyzeCotReport } from '../services/geminiService';
import { AnalysisResult, OrderFlowResult, TechnicalAnalysisResult, CotAnalysisResult } from '../types';
import { supabase } from '../services/supabase';

type ViewMode = 'FUNDAMENTAL' | 'ORDER_FLOW' | 'CHART' | 'COT';

interface DashboardProps {
  session: Session;
}

const Dashboard: React.FC<DashboardProps> = ({ session }) => {
  const navigate = useNavigate();
  const [inputPair, setInputPair] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [isPremium, setIsPremium] = useState(false);
  const [userTier, setUserTier] = useState<string>('Free'); // track exact plan
  const [profileLoading, setProfileLoading] = useState(true);
  const [showPlanModal, setShowPlanModal] = useState(false); // plan restriction modal
  const [recentSearches, setRecentSearches] = useState<string[]>(['EURUSD', 'GBPUSD', 'USDCAD']);
  const [viewMode, setViewMode] = useState<ViewMode>('FUNDAMENTAL');

  // Check user's actual subscription status
  useEffect(() => {
    const checkUserTier = async () => {
      if (!session?.user?.id) {
        setProfileLoading(false);
        return;
      }

      const { data: profile } = await supabase
        .from('user_profiles')
        .select('tier, subscription_status, plan_expires_at')
        .eq('id', session.user.id)
        .single();

      if (profile) {
        const hasPaidTier = ['Pro', 'Premium', 'Elite'].includes(profile.tier);
        const notExpired = !profile.plan_expires_at || new Date(profile.plan_expires_at) > new Date();
        const statusOk = !profile.subscription_status || profile.subscription_status === 'active' || profile.subscription_status === 'trial';
        setIsPremium(hasPaidTier && notExpired && statusOk);
        setUserTier(profile.tier || 'Free');
      } else {
        setIsPremium(false);
        setUserTier('Free');
      }
      setProfileLoading(false);
    };

    checkUserTier();
  }, [session]);

  // Results State
  const [fundResult, setFundResult] = useState<AnalysisResult | null>(null);
  const [flowResult, setFlowResult] = useState<OrderFlowResult | null>(null);
  const [techResult, setTechResult] = useState<TechnicalAnalysisResult | null>(null);
  const [cotResult, setCotResult] = useState<CotAnalysisResult | null>(null);

  // analysis features available for ALL paid plans (Pro, Premium, Elite)
  const canUseAnalysis = userTier === 'Pro' || userTier === 'Premium' || userTier === 'Elite';

  const handleAnalyze = async (pairToAnalyze: string) => {
    if (!pairToAnalyze.trim()) return;

    // Premium-only users cannot use AI analysis
    if (!canUseAnalysis) {
      setShowPlanModal(true);
      return;
    }

    setLoading(true);
    setError(null);

    // Save to Recent Searches
    const upperPair = pairToAnalyze.toUpperCase();
    setRecentSearches(prev => {
      const filtered = prev.filter(p => p !== upperPair);
      return [upperPair, ...filtered].slice(0, 3);
    });

    // Clear current view result
    if (viewMode === 'FUNDAMENTAL') setFundResult(null);
    if (viewMode === 'ORDER_FLOW') setFlowResult(null);
    if (viewMode === 'CHART') setTechResult(null);
    if (viewMode === 'COT') setCotResult(null);

    try {
      const pair = pairToAnalyze.toUpperCase();

      if (viewMode === 'FUNDAMENTAL') {
        const data = await analyzeForexPair(pair);
        setFundResult(data);
      } else if (viewMode === 'ORDER_FLOW') {
        const data = await analyzeOrderFlow(pair);
        setFlowResult(data);
      } else if (viewMode === 'CHART') {
        const data = await analyzeTechnicalTrend(pair);
        setTechResult(data);
      } else if (viewMode === 'COT') {
        const data = await analyzeCotReport(pair);
        setCotResult(data);
      }

    } catch (err: any) {
      if (err.message && err.message.includes("LIMIT_REACHED")) {
        setShowLimitModal(true);
      } else {
        setError(err.message || "An unexpected error occurred");
      }
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (mode: ViewMode) => {
    setViewMode(mode);
    setError(null);
  };

  // Show a minimal spinner while checking plan status
  if (profileLoading) {
    return (
      <main className="flex-grow flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-2 border-finance-accent border-t-transparent rounded-full animate-spin" />
          <p className="text-gray-400 text-sm">Verifying access...</p>
        </div>
      </main>
    );
  }

  // Free user gate
  if (!isPremium) {
    return (
      <main className="flex-grow max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8 relative">
        {/* Blurred content preview */}
        <div className="select-none pointer-events-none blur-sm opacity-40">
          <div className="h-14 bg-[#050505] rounded-xl mb-4 w-3/4" />
          <div className="h-96 bg-[#050505] rounded-xl mb-4" />
          <div className="grid grid-cols-3 gap-4">
            <div className="h-40 bg-[#050505] rounded-xl" />
            <div className="h-40 bg-[#050505] rounded-xl" />
            <div className="h-40 bg-[#050505] rounded-xl" />
          </div>
        </div>

        {/* Upgrade overlay */}
        <div className="absolute inset-0 flex flex-col items-center justify-center px-4">
          <div className="bg-finance-card/95 backdrop-blur-md border border-finance-accent/30 rounded-3xl p-10 max-w-lg w-full text-center shadow-2xl">
            <div className="w-16 h-16 rounded-full bg-finance-accent/10 flex items-center justify-center mx-auto mb-5">
              <svg className="w-8 h-8 text-finance-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h2 className="text-3xl font-extrabold text-white mb-3">Unlock Your Dashboard</h2>
            <p className="text-gray-400 mb-3 leading-relaxed">
              You're one step away from institutional-grade trading intelligence.
            </p>
            <p className="text-finance-accent font-semibold mb-8 text-sm">
              ⚡ 53 Elite Strategies · Institutional Signal Gatekeeper · Live Macro Bias
            </p>
            <button
              onClick={() => navigate('/pricing')}
              className="w-full bg-gradient-to-r from-finance-accent to-yellow-400 hover:from-yellow-400 hover:to-finance-accent text-finance-darker font-extrabold py-4 rounded-xl text-lg transition-all shadow-lg shadow-finance-accent/20 hover:shadow-finance-accent/40"
            >
              View Plans & Upgrade
            </button>
            <p className="text-gray-500 text-xs mt-4">Plans start at $30/month. Cancel anytime.</p>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="flex-grow max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8">

      {/* Plan Restriction Modal — shown when Premium user tries AI Analysis */}
      {showPlanModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-finance-card border border-orange-500/30 rounded-3xl p-10 max-w-md w-full text-center shadow-2xl">
            <div className="w-16 h-16 rounded-full bg-orange-500/10 flex items-center justify-center mx-auto mb-5">
              <svg className="w-8 h-8 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
              </svg>
            </div>
            <h2 className="text-2xl font-extrabold text-white mb-3">Analysis Quota Reached</h2>
            <p className="text-gray-400 mb-2 leading-relaxed">
              You've used all your AI analysis credits for this billing cycle.
            </p>
            <p className="text-gray-500 text-sm mb-7">
              Upgrade to <span className="text-yellow-400 font-semibold">Elite</span> for 250 analyses/month, or wait for your next billing cycle to reset.
            </p>
            <button
              onClick={() => { setShowPlanModal(false); navigate('/pricing'); }}
              className="w-full bg-gradient-to-r from-finance-accent to-yellow-400 hover:from-yellow-400 hover:to-finance-accent text-finance-darker font-extrabold py-3.5 rounded-xl text-base transition-all mb-3"
            >
              Upgrade My Plan
            </button>
            <button
              onClick={() => setShowPlanModal(false)}
              className="w-full text-gray-500 hover:text-gray-300 text-sm py-2 transition-colors"
            >
              Maybe later
            </button>
          </div>
        </div>
      )}

      {/* Input Section */}
      <section className="mb-8">
        <div className="flex flex-col md:flex-row gap-3 mb-6">
          <div className="flex-grow relative group">
            <input
              type="text"
              value={inputPair}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setTimeout(() => setIsFocused(false), 200)}
              onChange={(e) => setInputPair(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleAnalyze(inputPair);
                  setIsFocused(false);
                }
              }}
              placeholder="Enter pair (e.g., XAUUSD, EURUSD)"
              className="w-full bg-[#000000] border border-gray-800/60 text-white text-lg rounded-xl pl-12 pr-24 py-4 focus:ring-2 focus:ring-finance-accent/50 focus:border-finance-accent/30 outline-none transition-all duration-300 placeholder-gray-600 hover:border-gray-700"
            />
            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-600">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
              </svg>
            </div>

            {/* Clear Button */}
            <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-2">
              {inputPair && (
                <button
                  onClick={() => setInputPair('')}
                  className="text-gray-600 hover:text-white transition-colors p-1.5 rounded-lg hover:bg-gray-800 border border-transparent hover:border-gray-700/50"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>

            {/* Suggestions Dropdown */}
            {isFocused && recentSearches.length > 0 && (
              <div className="absolute top-[105%] left-0 w-full bg-[#000000] border border-gray-800/60 rounded-xl shadow-2xl z-50 overflow-hidden backdrop-blur-md">
                <div className="px-4 py-2.5 text-[11px] font-bold tracking-[2px] text-gray-600 uppercase bg-gray-900/50">Recent Searches</div>
                {recentSearches.map(pair => (
                  <div
                    key={pair}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      setInputPair(pair);
                      setIsFocused(false);
                    }}
                    className="px-4 py-3 hover:bg-gray-800/40 cursor-pointer text-gray-300 flex items-center justify-between transition-all border-t border-gray-800/30"
                  >
                    <div className="flex items-center gap-3">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 text-gray-600">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="font-semibold text-sm">{pair}</span>
                    </div>
                    <span className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Select</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <button
            onClick={() => handleAnalyze(inputPair)}
            disabled={loading}
            className={`px-8 py-4 rounded-xl font-black text-sm tracking-widest uppercase transition-all duration-300 shadow-lg whitespace-nowrap flex items-center gap-3
          ${loading
                ? 'bg-gray-800 cursor-not-allowed text-gray-500 border border-gray-700/50'
                : 'bg-finance-accent text-black hover:shadow-[0_0_30px_rgba(212,165,55,0.3)] hover:scale-[1.02] active:scale-[0.98]'
              }`}
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-gray-600 border-t-gray-400 rounded-full animate-spin"></div>
                Processing...
              </>
            ) : (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
                </svg>
                Analyze Edge
              </>
            )}
          </button>
        </div>

        {/* Navigation Tabs */}
        <div className="flex flex-wrap gap-1.5 p-1.5 bg-[#000000] rounded-xl w-full md:w-fit border border-gray-800/40 mb-6">
          <button
            onClick={() => switchMode('FUNDAMENTAL')}
            className={`px-4 py-2.5 rounded-lg text-xs font-bold tracking-wider uppercase transition-all duration-300 flex items-center gap-2 ${viewMode === 'FUNDAMENTAL'
              ? 'bg-[#151b2e] text-finance-accent shadow-sm border border-finance-accent/20'
              : 'text-gray-500 hover:text-gray-300 hover:bg-gray-800/30'
              }`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
            </svg>
            Fundamental Analysis
          </button>
          <button
            onClick={() => switchMode('ORDER_FLOW')}
            className={`px-4 py-2.5 rounded-lg text-xs font-bold tracking-wider uppercase transition-all duration-300 flex items-center gap-2 ${viewMode === 'ORDER_FLOW'
              ? 'bg-[#151b2e] text-finance-accent shadow-sm border border-finance-accent/20'
              : 'text-gray-500 hover:text-gray-300 hover:bg-gray-800/30'
              }`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3m0 0l.5 1.5m-.5-1.5h-9.5m0 0l-.5 1.5M9 11.25v1.5M12 9v3.75m3-6v6" />
            </svg>
            Order Flow Map
          </button>
          <button
            onClick={() => switchMode('CHART')}
            className={`px-4 py-2.5 rounded-lg text-xs font-bold tracking-wider uppercase transition-all duration-300 flex items-center gap-2 ${viewMode === 'CHART'
              ? 'bg-[#151b2e] text-finance-accent shadow-sm border border-finance-accent/20'
              : 'text-gray-500 hover:text-gray-300 hover:bg-gray-800/30'
              }`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
            </svg>
            Technical Chart
          </button>
          <button
            onClick={() => switchMode('COT')}
            className={`px-4 py-2.5 rounded-lg text-xs font-bold tracking-wider uppercase transition-all duration-300 flex items-center gap-2 ${viewMode === 'COT'
              ? 'bg-[#151b2e] text-finance-accent shadow-sm border border-finance-accent/20'
              : 'text-gray-500 hover:text-gray-300 hover:bg-gray-800/30'
              }`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
              <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 14.25v2.25m3-4.5v4.5m3-6.75v6.75m3-9v9M6 20.25h12A2.25 2.25 0 0020.25 18V6A2.25 2.25 0 0018 3.75H6A2.25 2.25 0 003.75 6v12A2.25 2.25 0 006 20.25z" />
            </svg>
            COT Positioning
          </button>
        </div>
      </section>

      {/* Error Message */}
      {error && (
        <div className="mb-8 p-4 bg-red-900/20 border border-red-900/50 rounded-lg flex items-center text-red-200">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 mr-3">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
          </svg>
          {error}
        </div>
      )}

      {/* Main Content Area */}
      <div className="flex flex-col items-center w-full max-w-5xl mx-auto pb-12">

        {/* Analysis Report Column */}
        <div className="w-full relative">

          <div className={!isPremium && (fundResult || flowResult || techResult || cotResult) ? 'filter blur-[10px] opacity-30 pointer-events-none select-none transition-all duration-1000' : ''}>
            <React.Suspense fallback={<div className="p-8 text-center text-gray-500 animate-pulse bg-[#000000] rounded-xl my-6">Loading analysis dashboard...</div>}>
              {viewMode === 'FUNDAMENTAL' && <ReportDisplay result={fundResult} loading={loading} />}
              {viewMode === 'ORDER_FLOW' && <OrderFlowDisplay result={flowResult} loading={loading} />}
              {viewMode === 'CHART' && <TechnicalChartDisplay result={techResult} loading={loading} />}
              {viewMode === 'COT' && <CotDisplay result={cotResult} loading={loading} />}
            </React.Suspense>
          </div>

          {/* Premium Blurred Overlay Content */}
          {!isPremium && (fundResult || flowResult || techResult || cotResult) && (
            <div className="absolute inset-0 flex flex-col items-center justify-start pt-16 z-20 pointer-events-auto">
              <div className="bg-[#000000] border border-finance-accent/30 p-8 rounded-2xl shadow-[0_0_80px_rgba(0,0,0,0.8)] text-center max-w-sm sm:max-w-md mx-4 backdrop-blur-md">
                <div className="w-16 h-16 bg-gradient-to-br from-finance-accent/20 to-yellow-600/10 text-finance-accent rounded-full flex items-center justify-center mx-auto mb-6 border border-finance-accent/20">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8"><path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" /></svg>
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">Premium Analysis</h3>
                <p className="text-gray-400 mb-8 text-sm leading-relaxed">This exclusive institutional insight and data extrapolation is for Premium Members only. Don't let the market move without you.</p>
                <button onClick={() => navigate('/pricing')} className="bg-finance-accent text-finance-dark font-black tracking-wide px-6 py-4 rounded-xl w-full hover:bg-yellow-500 transition-colors shadow-lg shadow-finance-accent/20">
                  UPGRADE NOW
                </button>
              </div>
            </div>
          )}

        </div>
      </div>

    </main>
  );
};

export default Dashboard;